import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FIELD_GROUPS } from '../../utils/manifest/manifestTemplateFields';
import { MANIFEST_SAMPLE_DATA } from '../../utils/manifest/manifestSampleData';
import '../../styles/ManifestPrint.css';

function fieldSet(fields) {
  return new Set((fields || []).map(String));
}

function ManifestPrintDocument({ fields, data }) {
  const selected = useMemo(() => fieldSet(fields), [fields]);
  const has = (key) => selected.has(key);

  const renderScalarFields = (groupFields) => {
    const rows = groupFields.filter((f) => has(f.key) && data[f.key] != null && data[f.key] !== '');
    if (!rows.length) return null;
    return (
      <div className="manifest-field-grid">
        {rows.map((f) => {
          const val = data[f.key];
          const fullWidth = String(val).length > 60 || f.key.includes('instructions');
          return (
            <div key={f.key} className={`manifest-field${fullWidth ? ' manifest-field-full' : ''}`}>
              <strong>{f.label}:</strong> {val}
            </div>
          );
        })}
      </div>
    );
  };

  const showFreightSection = FIELD_GROUPS.find((g) => g.group === 'Freight Details').fields.some(
    (f) => has(f.key)
  );

  const showPickupSection = FIELD_GROUPS.find((g) => g.group === 'Pickup Information').fields.some(
    (f) => has(f.key)
  );

  const showDeliverySection = FIELD_GROUPS.find((g) => g.group === 'Delivery Information').fields.some(
    (f) => has(f.key)
  );

  return (
    <div className="manifest-print-document">
      <h1>Freight Shipping Manifest</h1>
      <p className="manifest-subtitle">Provision Global Solutions</p>

      {FIELD_GROUPS.map((section) => {
        if (section.group === 'Freight Details' && showFreightSection) {
          return (
            <section key={section.group}>
              <h2>{section.group}</h2>
              {has('freight_table') && data.freight_lines?.length ? (
                <table>
                  <thead>
                    <tr>
                      <th>Line</th>
                      <th>Handling Unit</th>
                      <th>Pieces</th>
                      <th>Package Type</th>
                      <th>Description</th>
                      <th>NMFC</th>
                      <th>Class</th>
                      <th>Weight</th>
                      <th>Dimensions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.freight_lines.map((row) => (
                      <tr key={row.line}>
                        <td>{row.line}</td>
                        <td>{row.handling_unit}</td>
                        <td>{row.pieces}</td>
                        <td>{row.package_type}</td>
                        <td>{row.description}</td>
                        <td>{row.nmfc}</td>
                        <td>{row.freight_class}</td>
                        <td>{row.weight}</td>
                        <td>{row.dimensions}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}
              {renderScalarFields(
                section.fields.filter((f) => f.key !== 'freight_table')
              )}
            </section>
          );
        }

        if (section.group === 'Reference Numbers' && has('reference_numbers')) {
          return (
            <section key={section.group}>
              <h2>{section.group}</h2>
              <table>
                <thead>
                  <tr>
                    <th>Reference Type</th>
                    <th>Reference Number</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.reference_numbers || []).map((ref) => (
                    <tr key={`${ref.type}-${ref.number}`}>
                      <td>{ref.type}</td>
                      <td>{ref.number}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          );
        }

        if (section.group === 'Accessorials' && has('accessorials')) {
          return (
            <section key={section.group}>
              <h2>{section.group}</h2>
              <table>
                <thead>
                  <tr>
                    <th>Accessorial</th>
                    <th>Required</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.accessorials || []).map((row) => (
                    <tr key={row.name}>
                      <td>{row.name}</td>
                      <td>{row.required}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          );
        }

        if (section.group === 'Shipment Certification') {
          const certFields = section.fields.filter((f) => has(f.key));
          if (!certFields.length) return null;
          return (
            <section key={section.group}>
              <h2>{section.group}</h2>
              {has('certification_text') ? <p>{data.certification_text}</p> : null}
              {has('shipper_signature') ? (
                <div className="manifest-signature-block">
                  <div>
                    <strong>Shipper Signature:</strong>
                    <div className="manifest-signature-line" />
                    <div>
                      <strong>Printed Name:</strong> {data.shipper_signature_name}
                    </div>
                    <div>
                      <strong>Date:</strong> {data.shipper_signature_date}
                    </div>
                  </div>
                </div>
              ) : null}
              {has('driver_signature') ? (
                <div className="manifest-signature-block">
                  <div>
                    <strong>Driver Signature:</strong>
                    <div className="manifest-signature-line" />
                    <div>
                      <strong>Printed Name:</strong> {data.driver_signature_name}
                    </div>
                    <div>
                      <strong>Date:</strong> {data.driver_signature_date}
                    </div>
                  </div>
                </div>
              ) : null}
            </section>
          );
        }

        if (section.group === 'Carrier Use Only' && has('carrier_use_only')) {
          return (
            <section key={section.group}>
              <h2>{section.group}</h2>
              <div className="manifest-field-grid">
                <div className="manifest-field">
                  <strong>Pickup Time:</strong> __________________
                </div>
                <div className="manifest-field">
                  <strong>Trailer Loaded By:</strong> Shipper / Driver
                </div>
                <div className="manifest-field">
                  <strong>Freight Counted By:</strong> Shipper / Driver
                </div>
                <div className="manifest-field manifest-field-full">
                  <strong>Freight Condition at Pickup:</strong> Good / Damaged / Short
                </div>
                <div className="manifest-field manifest-field-full">
                  <strong>Exceptions Noted:</strong> ___________________________________________
                </div>
                <div className="manifest-field">
                  <strong>Carrier Representative:</strong> ___________________________
                </div>
                <div className="manifest-field">
                  <strong>Date:</strong> ___________________
                </div>
              </div>
            </section>
          );
        }

        if (
          section.group === 'Freight Details' ||
          section.group === 'Reference Numbers' ||
          section.group === 'Accessorials' ||
          section.group === 'Shipment Certification' ||
          section.group === 'Carrier Use Only'
        ) {
          return null;
        }

        if (section.group === 'Pickup Information' && !showPickupSection) return null;
        if (section.group === 'Delivery Information' && !showDeliverySection) return null;

        const visible = section.fields.some((f) => has(f.key));
        if (!visible) return null;

        return (
          <section key={section.group}>
            <h2>{section.group}</h2>
            {renderScalarFields(section.fields)}
          </section>
        );
      })}
    </div>
  );
}

/**
 * Full-screen printable preview for a manifest template.
 * @param {{ templateName: string, fields: string[], data?: object, onClose: () => void }} props
 */
const ManifestPrintPreview = ({ templateName, fields, data = MANIFEST_SAMPLE_DATA, onClose }) => {
  useEffect(() => {
    return () => {
      document.body.classList.remove('manifest-printing');
    };
  }, []);

  const handlePrint = () => {
    document.body.classList.add('manifest-printing');
    window.print();
    const onAfterPrint = () => {
      document.body.classList.remove('manifest-printing');
      window.removeEventListener('afterprint', onAfterPrint);
    };
    window.addEventListener('afterprint', onAfterPrint);
  };

  const content = !fields?.length ? (
    <div className="manifest-preview-overlay">
      <div className="manifest-preview-toolbar no-print">
        <h2>Manifest preview</h2>
        <button type="button" className="btn-secondary" onClick={onClose}>
          Close
        </button>
      </div>
      <div className="manifest-preview-scroll">
        <p style={{ padding: 24, background: '#fff', maxWidth: 480, margin: '24px auto' }}>
          Select at least one field in the template to preview.
        </p>
      </div>
    </div>
  ) : (
    <div className="manifest-preview-overlay">
      <div className="manifest-preview-toolbar no-print">
        <h2>Print preview — {templateName || 'Manifest'}</h2>
        <div className="manifest-preview-toolbar-actions">
          <button type="button" className="btn-primary" onClick={handlePrint}>
            Print
          </button>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
      <div className="manifest-preview-scroll">
        <ManifestPrintDocument fields={fields} data={data} />
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export { ManifestPrintDocument };
export default ManifestPrintPreview;
